import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Waves, Bell, LifeBuoy, ChevronDown, LogOut, Loader2, X, Upload,
  LayoutDashboard, Package, FileText, ImageIcon, ClipboardCheck, Sparkles, HelpCircle,
} from "lucide-react";
import {
  Overview, MyPurchases, Documents, Installation, MyForms, CareAccessories, Support,
} from "./PortalSections";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "purchases", label: "My Purchases", icon: Package },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "installation", label: "Installation", icon: ImageIcon },
  { key: "forms", label: "My Forms", icon: ClipboardCheck },
  { key: "care", label: "Care & Accessories", icon: Sparkles },
  { key: "support", label: "Support", icon: HelpCircle },
];

function Logo() {
  return (
    <div className="flex items-center gap-2.5" data-testid="portal-logo">
      <span className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-sm"><Waves className="w-5 h-5" /></span>
      <div className="leading-tight">
        <div className="text-[15px] font-extrabold text-slate-900 tracking-tight">Hot Tub Superstore</div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-indigo-500">Customer Portal</div>
      </div>
    </div>
  );
}

export default function CustomerPortal() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCat, setReportCat] = useState("Fault");
  const [extraRequests, setExtraRequests] = useState([]);

  useEffect(() => {
    document.title = "Hot Tub Superstore — Customer Portal";
    api.get("/portal/demo").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  const notify = (msg) => toast.info(msg);
  const openReport = (cat) => { setReportCat(cat || "Fault"); setReportOpen(true); };

  if (data === false) return <div className="min-h-screen flex items-center justify-center text-slate-500">Unable to load the portal demo.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading your portal…</div>;

  const requests = [...extraRequests, ...data.support.requests];

  return (
    <div className="min-h-screen bg-[#f6f7fb] flex flex-col" data-testid="customer-portal">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="hidden sm:inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide" data-testid="portal-demo-label">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> Customer Portal Demo
            </span>
            <button className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 relative" title="Notifications" onClick={() => notify("You have no new notifications (demonstration).")} data-testid="portal-notifications">
              <Bell className="w-[18px] h-[18px]" /><span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-rose-500" />
            </button>
            <button className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Help & Support" onClick={() => setTab("support")} data-testid="portal-help">
              <LifeBuoy className="w-[18px] h-[18px]" />
            </button>
            <div className="relative">
              <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100" data-testid="portal-profile-menu">
                <span className="w-8 h-8 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">{data.customer.first_name[0]}{data.customer.last_name[0]}</span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-40" data-testid="portal-profile-dropdown">
                  <div className="px-4 py-2 border-b border-slate-100"><div className="text-sm font-semibold text-slate-900">{data.customer.first_name} {data.customer.last_name}</div><div className="text-xs text-slate-500 truncate">{data.customer.email}</div></div>
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={() => { setMenuOpen(false); notify("Profile (demonstration)."); }}>My profile</button>
                  <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => notify("Sign out is disabled in this demonstration.")} data-testid="portal-signout"><LogOut className="w-4 h-4" /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Nav */}
        <nav className="max-w-6xl mx-auto px-2 sm:px-6 flex gap-1 overflow-x-auto" data-testid="portal-nav">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Welcome */}
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="portal-welcome">Welcome back, {data.customer.first_name}</h1>
        <p className="text-sm text-slate-500 mt-0.5">Here's everything about your {data.product_model}.</p>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-6 flex-1">
        {tab === "overview" && <Overview data={data} setTab={setTab} onContact={() => openReport("Question")} />}
        {tab === "purchases" && <MyPurchases data={data} notify={notify} />}
        {tab === "documents" && <Documents data={data} notify={notify} />}
        {tab === "installation" && <Installation data={data} />}
        {tab === "forms" && <MyForms data={data} notify={notify} />}
        {tab === "care" && <CareAccessories data={data} notify={notify} />}
        {tab === "support" && <Support data={data} requests={requests} onReport={openReport} />}
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs text-slate-400 flex flex-wrap items-center justify-between gap-2">
          <span>Hot Tub Superstore · Customer Portal (Demonstration)</span>
          <span>Prototype — no live customer data is shown.</span>
        </div>
      </footer>

      {reportOpen && (
        <ReportModal category={reportCat} products={data.purchases.map((p) => p.name)}
          onClose={() => setReportOpen(false)}
          onSubmitted={(req) => { setExtraRequests((prev) => [req, ...prev]); setReportOpen(false); setTab("support"); toast.success(`Request ${req.id} submitted (demonstration).`); }} />
      )}
    </div>
  );
}

function ReportModal({ category, products, onClose, onSubmitted }) {
  const [form, setForm] = useState({ product: products[0] || "", category, description: "", started: "", urgency: "Normal", subject: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/portal/demo/support", {
        product: form.product, category: form.category, subject: form.subject || `${form.category}: ${form.product}`,
        description: form.description, started: form.started, urgency: form.urgency,
      });
      onSubmitted(r.data.request);
    } catch (e) {
      toast.error("Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="portal-report-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="text-base font-bold text-slate-900">{category === "Fault" ? "Report a Fault" : category === "Service" ? "Request a Service" : "Ask a Question"}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="portal-report-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <L label="Purchased product">
            <select value={form.product} onChange={(e) => set("product", e.target.value)} className="portal-input" data-testid="report-product">
              {products.map((p) => <option key={p}>{p}</option>)}
            </select>
          </L>
          <L label="Problem category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="portal-input" data-testid="report-category">
              {["Fault", "Service", "Question", "Water care", "Cover", "Electrical", "Accessory"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </L>
          <L label="Description">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Tell us what's happening…" className="portal-input" data-testid="report-description" />
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="When did it start?"><input type="date" value={form.started} onChange={(e) => set("started", e.target.value)} className="portal-input" data-testid="report-started" /></L>
            <L label="Urgency">
              <select value={form.urgency} onChange={(e) => set("urgency", e.target.value)} className="portal-input" data-testid="report-urgency">
                {["Low", "Normal", "High", "Urgent"].map((u) => <option key={u}>{u}</option>)}
              </select>
            </L>
          </div>
          <L label="Photos or video (optional)">
            <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 cursor-pointer hover:border-indigo-300">
              <Upload className="w-4 h-4" /> <span>Attach files (demonstration only)</span>
              <input type="file" multiple accept="image/*,video/*" className="hidden" data-testid="report-upload" />
            </label>
          </L>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="report-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit request
          </Button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
